import * as Sentry from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { accountApi } from '@/api/accountApi'
import { getApiError } from '@/api/helpers/apiError'
import { useSession } from '@/session/SessionContext'
import { showToast } from '@/theme/notifications'
const { logger } = Sentry

const getLoginError = (status?: number) => {
  if (status === 401) {
    return 'login.errors.invalidCredentials'
  }

  if (status === 429) {
    return 'login.errors.tooManyRequests'
  }

  return 'login.errors.other'
}

type MfaLoginState = {
  isMfaRequired: boolean
  uid: string
  resendKey?: string
  email: string
}

export const useMfaLogin = () => {
  const intl = useIntl()
  const [state, setState] = useState<MfaLoginState | null>(null)

  const { refreshSession, isRefreshing } = useSession()

  const { mutate, isPending } = useMutation({
    mutationFn: accountApi.login,
    onMutate: () => {
      setState(null)
    },
    onSuccess: (data, variables) => {
      setState({
        uid: data.uid,
        resendKey: data.resend_key,
        isMfaRequired: data.mfa_required,
        email: variables.email
      })

      logger.info('login_success', {
        data: {
          isMfaRequired: data.mfa_required
        }
      })

      if (!data.mfa_required) {
        void refreshSession()
      }
    },
    onError: (error) => {
      logger.error('login_error', {
        data: {
          error: error.message
        }
      })

      const { status } = getApiError(error)
      const message = getLoginError(status)
      showToast(intl.formatMessage({ id: message }), 'error')
    }
  })

  const result = useMemo(
    () => ({
      login: mutate,
      state,
      isPending: isPending || isRefreshing
    }),
    [mutate, state, isPending, isRefreshing]
  )

  return result
}
